import React, { useEffect, useState } from "react";
import { organizationRepo } from "../../services";
import { BUSINESS_MODELS } from "../../config/business-models";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { Avatar, Badge, Button, Card, Input, PageHead, Select, Switch, Textarea } from "../../components/ui";
import { DOC_TYPES, cleanPrefix } from "../../services/docNumber";
import { digitsOnly, normalizeBranch, validateThaiTaxId } from "../../utils/thaiTax";
import ShopLinkCard from "../../components/ShopLinkCard";

// เลขที่เอกสารที่ร้านแก้ตัวอักษรนำหน้าได้ (เลขลำดับ/ปีระบบกำหนดเอง)
const EDITABLE_PREFIX = ["tax_invoice", "receipt_tax_invoice", "credit_note", "invoice", "receipt"];

export default function Organization() {
  const { organization, refreshOrganizations, toast, hasModule } = useApp();
  const { tx, pick } = useI18n();
  const [form, setForm] = useState(organization || {});
  const [saving, setSaving] = useState(false);
  const etax = hasModule("etax");

  useEffect(() => setForm(organization || {}), [organization]);

  const save = async () => {
    if (!organization) return;
    // ข้อมูลภาษี (เฉพาะร้านที่มี e-Tax) — ตรวจก่อนบันทึก ไม่ให้ใบกำกับภาษีออกด้วยเลขผิด
    let taxPatch = {};
    if (etax) {
      const taxId = digitsOnly(form.taxId);
      if ((form.vatRegistered || taxId) && !validateThaiTaxId(taxId)) {
        return toast(tx("เลขประจำตัวผู้เสียภาษีไม่ถูกต้อง (13 หลัก ตรวจ checksum ไม่ผ่าน)", "Invalid tax ID (13 digits, checksum failed)"), "error");
      }
      const branchCode = normalizeBranch(form.branchCode);
      if (branchCode === null) return toast(tx("รหัสสาขาต้องเป็นตัวเลขไม่เกิน 5 หลัก", "Branch code must be up to 5 digits"), "error");
      const docPrefixes = {};
      for (const type of EDITABLE_PREFIX) {
        const raw = String(form.docPrefixes?.[type] || "").trim();
        if (raw && !cleanPrefix(raw)) return toast(tx("อักษรนำหน้าเลขที่ใช้ได้เฉพาะ A-Z 0-9 ไม่เกิน 8 ตัว", "Prefix: A-Z 0-9, up to 8 characters"), "error");
        if (raw) docPrefixes[type] = cleanPrefix(raw);
      }
      taxPatch = { taxId, branchCode, branchName: String(form.branchName || "").trim(), vatRegistered: !!form.vatRegistered, docPrefixes };
    }
    setSaving(true);
    // ไม่ส่งฟีเจอร์เสริม (addons) และแบรนด์ (branding) กลับไป — ฟอร์มที่เปิดค้างไว้จะทับรายการที่เพิ่งซื้อ/ที่เพิ่งตั้งจากหน้าอื่น
    const { addons, branding, ...rest } = form;
    await organizationRepo.update(null, organization.id, { ...rest, ...taxPatch });
    await refreshOrganizations();
    setSaving(false);
    toast(tx("บันทึกข้อมูลองค์กรเรียบร้อย", "Organization saved"), "success");
  };

  if (!organization) return null;

  return (
    <>
      <PageHead title={tx("ข้อมูลองค์กร", "Organization settings")} subtitle={tx("ข้อมูลนี้จะแสดงบนเอกสาร ใบเสร็จ และหน้าลูกค้า", "Used on documents, receipts and the customer app")} actions={<Button variant="primary" onClick={save} disabled={saving}>{tx("บันทึก", "Save")}</Button>} />

      <div className="grid-sidebar">
        <Card>
          <div className="u-center">
            <Avatar name={organization.name} size="lg" />
            <div className="u-strong u-mt-8">{organization.name}</div>
            <Badge tone="indigo">{organization.plan}</Badge>
          </div>
          <div className="u-fs-13 u-mt-16" style={{ display: "grid", gap: 6 }}>
            <div className="u-between"><span className="u-muted">{tx("โมเดลธุรกิจ", "Business model")}</span><span>{pick(BUSINESS_MODELS.find((m) => m.key === organization.businessModel))}</span></div>
            <div className="u-between"><span className="u-muted">{tx("เลขผู้เสียภาษี", "Tax ID")}</span><span>{organization.taxId}</span></div>
          </div>
        </Card>

        <ShopLinkCard />

        <Card>
          <Input label={tx("ชื่อธุรกิจ (ไทย)", "Business name (TH)")} value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input label={tx("ชื่อธุรกิจ (อังกฤษ)", "Business name (EN)")} value={form.nameEn || ""} onChange={(e) => setForm({ ...form, nameEn: e.target.value })} />
          <div className="grid-2">
            <Input label={tx("เบอร์โทร", "Phone")} value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <Input label={tx("อีเมล", "Email")} value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          {/* ร้านที่มี e-Tax แก้เลขผู้เสียภาษีที่การ์ด "ข้อมูลภาษี" ด้านล่าง (มีตรวจ checksum) */}
          {etax ? null : <Input label={tx("เลขผู้เสียภาษี", "Tax ID")} value={form.taxId || ""} onChange={(e) => setForm({ ...form, taxId: e.target.value })} />}
          <Input label="LINE OA" value={form.lineOa || ""} onChange={(e) => setForm({ ...form, lineOa: e.target.value })} />
          <Textarea label={tx("ที่อยู่", "Address")} value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          <Select
            label={tx("โมเดลธุรกิจ", "Business model")}
            options={BUSINESS_MODELS.map((model) => ({ value: model.key, label: pick(model) }))}
            value={form.businessModel}
            onChange={(e) => setForm({ ...form, businessModel: e.target.value })}
          />
        </Card>

        {etax ? (
          <Card title={tx("ข้อมูลภาษี (สำหรับใบกำกับภาษี)", "Tax details (for tax invoices)")} subtitle={tx("แสดงบนใบกำกับภาษีที่ออกจากระบบ — ออกแล้วแก้ย้อนหลังไม่ได้ จึงตรวจให้ถูกก่อน", "Printed on issued tax invoices — they cannot be edited afterwards")}>
            <Switch label={tx("ร้านจดทะเบียนภาษีมูลค่าเพิ่ม (VAT)", "VAT registered")} checked={!!form.vatRegistered} onChange={(checked) => setForm({ ...form, vatRegistered: checked })} />
            {!form.vatRegistered ? (
              <div className="field-hint">{tx("ไม่จด VAT = ออกได้เฉพาะใบเสร็จรับเงินธรรมดา ยังออกใบกำกับภาษีไม่ได้", "Not VAT registered: ordinary receipts only")}</div>
            ) : null}
            <div className="grid-2 u-mt-8">
              <Input
                label={tx("เลขประจำตัวผู้เสียภาษี (13 หลัก)", "Tax ID (13 digits)")}
                inputMode="numeric"
                maxLength={13}
                value={form.taxId || ""}
                error={form.taxId && !validateThaiTaxId(digitsOnly(form.taxId)) ? tx("เลขไม่ถูกต้อง", "Invalid tax ID") : undefined}
                onChange={(e) => setForm({ ...form, taxId: digitsOnly(e.target.value).slice(0, 13) })}
              />
              <Input label={tx("รหัสสาขา (ว่าง = สำนักงานใหญ่ 00000)", "Branch code (blank = HQ)")} inputMode="numeric" maxLength={5} value={form.branchCode || ""} onChange={(e) => setForm({ ...form, branchCode: digitsOnly(e.target.value).slice(0, 5) })} />
            </div>
            <Input label={tx("ชื่อสาขา (ถ้ามี)", "Branch name")} value={form.branchName || ""} onChange={(e) => setForm({ ...form, branchName: e.target.value })} />
            <div className="u-strong u-fs-13 u-mt-16 u-mb-8">{tx("อักษรนำหน้าเลขที่เอกสาร", "Document number prefix")}</div>
            <div className="grid-2">
              {EDITABLE_PREFIX.map((type) => (
                <Input
                  key={type}
                  label={`${DOC_TYPES[type].label} (${tx("ค่าเริ่มต้น", "default")} ${DOC_TYPES[type].prefix})`}
                  maxLength={8}
                  placeholder={DOC_TYPES[type].prefix}
                  value={form.docPrefixes?.[type] || ""}
                  onChange={(e) => setForm({ ...form, docPrefixes: { ...(form.docPrefixes || {}), [type]: e.target.value.toUpperCase() } })}
                />
              ))}
            </div>
            <div className="field-hint">{tx("รูปแบบ PREFIX-ปี ค.ศ.-ลำดับ 6 หลัก เช่น TX-2026-000001 · เปลี่ยนอักษรนำหน้ามีผลกับเอกสารที่ออกใหม่เท่านั้น ลำดับนับต่อเนื่อง", "Format PREFIX-YEAR-000001. Changes apply to new documents only.")}</div>
          </Card>
        ) : null}
      </div>
    </>
  );
}
